export function toCategorySlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function categoryLandingPath(category) {
  if (category?.is_published === false || category?.seo?.is_published === false) {
    return category?.id ? `/shop?categoryId=${encodeURIComponent(category.id)}` : "/shop";
  }
  const slug = category?.slug || toCategorySlug(category?.name);
  return slug ? `/category/${encodeURIComponent(slug)}` : "/shop";
}

export function isPublishedCategory(category) {
  if (!category) return false;
  if (category.is_published === false || category.published === false || category.seo?.is_published === false) return false;
  if (category.status && String(category.status).toLowerCase() !== "published") return false;
  return true;
}

export function isIndexableCategory(category) {
  return isPublishedCategory(category) && category?.seo?.is_indexable !== false;
}

export function categoryDisplayName(category) {
  return String(category?.name || category?.title || "Rental Outfits").trim();
}

function asArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function normalizeTitleBodyItems(value, fallbackItems) {
  const items = asArray(value)
    .map((item) => ({
      title: String(item?.title || item?.label || "").trim(),
      body: String(item?.body || item?.description || "").trim(),
    }))
    .filter((item) => item.title || item.body);
  return items.length ? items : fallbackItems;
}

function normalizeFaqs(value, name) {
  const faqs = asArray(value)
    .map((item) => ({
      q: String(item?.q || item?.question || item?.title || "").trim(),
      a: String(item?.a || item?.answer || item?.body || item?.description || "").trim(),
    }))
    .filter((item) => item.q && item.a);
  if (faqs.length) return faqs;
  return [
    {
      q: `How do I rent ${name.toLowerCase()}?`,
      a: `Choose your ${name.toLowerCase()} look, select dates on the product page and complete checkout. Our team confirms fit details before dispatch or pickup.`,
    },
    {
      q: `Can I style ${name.toLowerCase()} for more than one occasion?`,
      a: `Yes. Each piece can be styled for different dress codes, from intimate gatherings to premium festive and wedding events.`,
    },
    {
      q: "Are cleaning and care included?",
      a: "Yes. Rentals are quality checked and professionally prepared so you can focus on the event, not garment care.",
    },
  ];
}

export function normalizeCategoryLinks(value) {
  return asArray(value)
    .map((item) => ({
      label: String(item?.label || item?.name || item?.title || "").trim(),
      url: String(item?.url || categoryLandingPath(item)).trim(),
    }))
    .filter((item) => item.label && item.url);
}

export function normalizeProductLinks(value) {
  return asArray(value)
    .map((item) => ({
      label: String(item?.label || item?.name || item?.title || "").trim(),
      url: String(item?.url || (item?.id ? `/product/${item.id}` : "")).trim(),
    }))
    .filter((item) => item.label && item.url);
}

export function buildCategoryLandingContent(category, products = []) {
  const seo = category?.seo || {};
  const name = categoryDisplayName(category);
  const count = Array.isArray(products) ? products.length : 0;
  const plural = count === 1 ? "look" : "looks";
  const title = seo.seo_title || `${name} Rentals | Gem Fashion Studios`;
  const description =
    seo.meta_description ||
    `Explore ${name.toLowerCase()} on rent with curated fits, transparent rental steps and occasion-ready styling from Gem Fashion Studios.`;
  const intro =
    seo.introduction ||
    category?.description ||
    `Discover ${name.toLowerCase()} rentals curated for celebrations, photoshoots and statement entrances, with ${count || "multiple"} ${plural} ready to browse online.`;

  const sections = normalizeTitleBodyItems(seo.sections, [
    {
      title: `Why rent ${name}?`,
      body: "Enjoy premium fashion for important moments without adding another rarely repeated outfit to your wardrobe.",
    },
  ]);
  const occasions = normalizeTitleBodyItems(seo.occasions, [
    { title: "Wedding and reception styling", body: "Choose refined silhouettes, rich textures and statement accessories for polished wedding guest looks." },
    { title: "Festive and family events", body: "Rent standout outfits without repeating your wardrobe across celebrations, pujas and festive dinners." },
    { title: "Photoshoots and special entries", body: "Pick camera-ready pieces that hold shape, color and detail for portraits, reels and grand entrances." },
  ]);
  const rentalProcess = normalizeTitleBodyItems(seo.rental_process, [
    { title: "1. Select your look", body: "Open a product page, review photos, rent price and available sizing details." },
    { title: "2. Confirm dates and fit", body: "Share event dates and fit notes so the team can help prepare the right rental experience." },
    { title: "3. Wear, return, relax", body: "Enjoy premium fashion for the moment, then return it after the booking window." },
  ]);
  const rentalBenefits = normalizeTitleBodyItems(seo.rental_benefits, [
    { title: "Flexible occasion dressing", body: "Benefits include lower cost per occasion, more variety, less wardrobe clutter and professionally prepared outfits." },
  ]);
  const cta = {
    label: String(seo.cta?.label || `Start renting ${name}`).trim(),
    url: String(seo.cta?.url || (category?.id ? `/shop?categoryId=${category.id}` : "/shop")).trim(),
    body: String(seo.cta?.body || "Browse the collection, shortlist your favorite products and reserve the look for your next occasion.").trim(),
  };

  return {
    title,
    description,
    intro,
    sections,
    occasions,
    rentalProcess,
    rentalBenefits,
    faqs: normalizeFaqs(seo.faqs, name),
    relatedCategoryLinks: normalizeCategoryLinks(seo.related_category_links),
    relatedProductLinks: normalizeProductLinks(seo.related_product_links),
    cta,
  };
}

export function productImage(product) {
  const images = Array.isArray(product?.images) ? product.images : [];
  return product?.first_image || product?.image_url || images[0] || "";
}

export function productPriceLabel(product) {
  if (product?.min_price_paise != null) return `₹${Math.round(Number(product.min_price_paise) / 100).toLocaleString("en-IN")}`;
  if (product?.price != null) return `₹${Number(product.price).toLocaleString("en-IN")}`;
  return "View rental details";
}
